// Criminal-defense workspace.
// ClientHero + AISummary + SwimLanes + DocWall (shared) + a 2x2 grid of
// criminal-specific cards: Charges, Motions, Plea/Padilla, Sentencing.

import type { Client, Matter, PracticeArea } from "../../../data/types";
import { AISummary } from "../AISummary";
import { MatterHero } from "../MatterHero";
import { DocWall } from "../DocWall";
import { SwimLanes } from "../SwimLanes";
import {
  pick,
  type BailShape,
  type ChargeShape,
  type CourtDateShape,
  type ImmigrationImpactShape,
} from "../workspace-shapes";
import { ChargesPanel } from "./ChargesPanel";
import { MotionsPanel } from "./MotionsPanel";
import { PleaStatus } from "./PleaStatus";
import { SentencingCard } from "./SentencingCard";
import type { MatterWithClient } from "../../../api/mock";

interface Props {
  matter: Matter;
  client: Client | null | undefined;
  pa: PracticeArea | null;
  templateId: string | null;
}

interface MotionShape {
  title: string;
  status?: "pending" | "granted" | "denied" | "filed";
  filedDate?: string;
}

interface SentencingShape {
  scheduled?: string;
  outcome?: string;
  guidelinesRange?: string;
  imposed?: string;
}

interface PleaShape {
  status?: string;
  offer?: string;
  counterOffer?: string;
  acceptedDate?: string;
}

export function CriminalWorkspace({ matter, client, pa, templateId }: Props) {
  const ws = matter.workspace;
  const charges = pick<ChargeShape[]>(ws, "charges");
  const motions = pick<MotionShape[]>(ws, "motions");
  const plea = pick<PleaShape>(ws, "plea");
  const bail = pick<BailShape>(ws, "bail");
  const impact = pick<ImmigrationImpactShape>(ws, "immigrationImpact");
  const sentencing = pick<SentencingShape>(ws, "sentencing");
  const courtDates = pick<CourtDateShape[]>(ws, "courtDates");
  const caseId = (matter as Partial<MatterWithClient>).mongoId;

  return (
    <>
      <MatterHero matter={matter} client={client} pa={pa} templateId={templateId} />
      <AISummary summary={matter.aiSummary} caseId={caseId} />
      <SwimLanes matter={matter} pa={pa} />
      <DocWall documents={matter.documents} caseId={caseId} driveFolder={matter.driveFolder} />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: 14,
        }}
      >
        <ChargesPanel charges={charges} />
        <MotionsPanel motions={motions} />
        <PleaStatus plea={plea} bail={bail} impact={impact} />
        <SentencingCard sentencing={sentencing} courtDates={courtDates} />
      </div>
    </>
  );
}
